/** @jsxImportSource @emotion/react */
import React from "react";
import { Form, Select } from "antd";
import { MapNodeEditor } from "../MapNodeEditor";
import * as Styles from "./styles";
import { DraftConfigNode, DraftMapConfigNode, DraftOption } from "../types";
import { BASE_OPTION_NAME } from "../../../../usecases/HomeConfig/constant";
import { UnitOfMeasurement } from "../../../../usecases/HomeConfig/types";
import { HomeConfigUtil } from "../../../../usecases/HomeConfig/utils";

interface BaseOptionEditorProps {
  visible: boolean;
  options: DraftOption[];
  onOptionsChange: React.Dispatch<React.SetStateAction<DraftOption[]>>;
}

const UNIT_OF_MEASUREMENT_OPTIONS = Object.values(UnitOfMeasurement).map(
  (unit) => ({ label: unit, value: unit }),
);

const isMapNode = (node: DraftConfigNode): node is DraftMapConfigNode => {
  return node.type === "map";
};

export const BaseOptionEditor = ({
  visible,
  options,
  onOptionsChange,
}: BaseOptionEditorProps) => {
  React.useEffect(() => {
    if (!visible) {
      return;
    }
    // NOTE: the baseOption variant always edits one reserved option; seed it so
    // the drawer never renders with nothing to bind the map editor to. Keyed on
    // `options` because the drawer's own reset effect runs after this one and
    // would otherwise wipe a seed keyed on `visible` alone.
    onOptionsChange((prev) => {
      const hasBaseOption = prev.some(HomeConfigUtil.isBaseOption);
      if (hasBaseOption) {
        return prev;
      }
      const seededBaseOption: DraftOption = {
        name: BASE_OPTION_NAME,
        order: 0,
        customGroup: "",
        price: 0,
        dependency: {},
        useHighlightEffect: false,
        show2DMap: false,
        highlightGroup: 0,
        nodes: [
          { type: "map", filterBy: {}, floorMaps: [], areaHighlights: [] },
        ],
      };
      return [...prev, seededBaseOption];
    });
  }, [visible, options, onOptionsChange]);

  const baseOptionIndex = options.findIndex(HomeConfigUtil.isBaseOption);
  const baseOption = options[baseOptionIndex];
  // NOTE: the index pins which node handleBaseMapChange writes to, so a base
  // config carrying more than one map node cannot have them all overwritten.
  const baseMapNodeIndex = baseOption?.nodes.findIndex(isMapNode) ?? -1;
  const baseMapNode = baseOption?.nodes.find(isMapNode);

  const handleBaseMapChange = (
    updater: (node: DraftMapConfigNode) => DraftMapConfigNode,
  ) => {
    onOptionsChange((prev) => {
      return prev.map((option, index) => {
        if (index !== baseOptionIndex) {
          return option;
        }
        const updatedNodes = option.nodes.map((node, nodeIndex) => {
          if (nodeIndex !== baseMapNodeIndex || !isMapNode(node)) {
            return node;
          }
          return updater(node);
        });
        return { ...option, nodes: updatedNodes };
      });
    });
  };

  const handleUnitOfMeasurementChange = (value: UnitOfMeasurement) => {
    onOptionsChange((prev) => {
      return prev.map((option, index) => {
        return index === baseOptionIndex
          ? { ...option, unitOfMeasurement: value }
          : option;
      });
    });
  };

  if (!baseMapNode) {
    return null;
  }

  return (
    <>
      <Form.Item label="Metric Measurement">
        <Select
          placeholder="Select measurement"
          value={baseOption?.unitOfMeasurement}
          options={UNIT_OF_MEASUREMENT_OPTIONS}
          onChange={handleUnitOfMeasurementChange}
        />
      </Form.Item>
      <div css={Styles.nodeEditorStyle}>
        <MapNodeEditor
          node={baseMapNode}
          showFloorName
          onChange={handleBaseMapChange}
        />
      </div>
    </>
  );
};
